<template>
  <MainLayout fluid>
    <template v-if="location">
      <div class="row">
        <div class="col">
          <h1 @click="selectLocation" class="linkish">
            <i :class="['bx', icon]"></i>
            <span class="ml-1" v-text="locationName" />
          </h1>
        </div>
        <div v-if="displayStats.length" class="col h4 d-flex flex-column flex-lg-row align-items-center justify-content-end">
          <h4 v-for="(stat,i) in displayStats" :key="i" class="ml-4"><strong>{{ stat.value }}</strong> <small class="text-muted">{{ $t(stat.label) }}</small></h4>
        </div>
      </div>
      <b-input-group size="lg" class="mb-4">
        <template #prepend>
          <b-input-group-text>
            <i class='bx bx-scan' :class="{'bx-spin': loading}" ></i>
          </b-input-group-text>
        </template>
        <b-input 
          ref="barcode"
          autofocus 
          autocomplete="off"
          :placeholder="$t(`${namespace}.input-placeholder`)"
          v-model="internalBarcode"
          @keypress="preventIllegal"
          @keyup.enter="getPackage"
          @paste="pasted"

        ></b-input>
        <b-input-group-append>
          <b-button @click="quickCheckin()" variant="primary" class="d-flex align-items-center">
            <i class='bx bxs-bolt' ></i>
            <span class="d-none d-xl-inline ml-2">{{ $t('stores.quick-checkin.title') }}</span>
          </b-button>
        </b-input-group-append>
        <b-input-group-append v-if="hasInventory && location">
          <b-dropdown no-caret>
            <template #button-content>
              <i class='bx bx-menu' ></i>
            </template>
            <b-dropdown-item @click="viewLayout()">
              <i class='bx bx-layout' ></i>
              {{ $t('stores.inventory.view-layout') }}
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item @click="downloadLabels()">
              <i class='bx bx-printer' ></i>
              {{ $t('stores.inventory.print-labels') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-input-group-append>
      </b-input-group>      

      <div class="d-flex flex-column flex-xl-row mb-4">
        <div class="pr-4">
          <i class='bx bx-layer h3 mr-3 d-none d-md-inline' ></i>
          <b-button-group class="flex-wrap">
            <b-button 
              v-for="option in groupingOptions" 
              :key="option.value" 
              pill 
              @click="setGroupBy(option)" 
              size="sm" 
              :variant="groupBy == option ? 'primary' : null"
              class="mr-2 mb-2"
            >
              <i v-if="option.icon" :class="['bx', option.icon]"></i>
              {{ option.label }}
              <i v-if="groupBy == option" :class="['bx', reversed ? 'bx-sort-down' : 'bx-sort-up']"></i>
            </b-button>
          </b-button-group>
        </div>
        <hr class="d-xl-none">
        <div v-if="hasInventory" class="pr-4">
          <i class='bx bx-filter h3 mr-3 d-none d-md-inline' ></i>
          <b-button-group class="flex-wrap">
            <b-button 
            v-for="option in inventoryFilterOptions" 
              :key="option.value" 
              pill 
              @click="setInventoryFilter(option)" 
              size="sm" 
              :variant="filterBy == option ? 'primary' : null"
              class="mr-2 mb-2"
            >
              <i v-if="option.icon" :class="['bx', option.icon]"></i>
              {{ option.label }}
            </b-button>
          </b-button-group>
        </div>
      </div>


      <div class="clearfix">
        <h3 v-if="packages.length" class="mb-4">
          <i class='bx bx-package'></i> 
          {{$t('stores.packages')}}
          <b-badge variant="info">{{filteredPackages.length}}</b-badge>
        </h3>
      </div>

      <b-container fluid class="p-0 mb-4" v-for="(p,group) in orderedPackages" :key="group">
        <div class="bg-light border rounded">
          <h5 class="m-4">
            <i :class="['bx', groupBy.icon]"></i> 
            {{groupBy.format ? groupBy.format(group) : group}} 
            <b-badge variant="info">{{p.length}}</b-badge>
          </h5>

          <b-row class="px-4">
            <b-col v-for="item in p" :key="item.id" cols="12" sm="6" lg="4" xl="3">
              <PackageInfo v-bind="{item, location}" @click="openPackage(item)" :detail="infoDetail" :locker-numbers="hasInventory" />
            </b-col>
          </b-row>
        </div>
      </b-container>

    </template>

    <!-- <pre><code>{{ location }}</code></pre> -->

  </MainLayout>
</template>

<script>
import MainLayout from "@/layouts/Main.vue";
import api from '@/api';
import Package from '@/resources/Package';
import SelectItem from "@/components/Modals/SelectItem.vue";
import QuickCheckin from "@/components/Modals/QuickCheckin.vue";
import PackageInfo from "@/components/PackageInfo.vue";
import moment from 'moment';
import { vibrate } from '@/utils';
import { CLASS_PUDO, CLASS_WAREHOUSE, FEATURE_INVENTORY } from "@/resources/Location";
import InventoryLayout from "@/components/Modals/InventoryLayout.vue";
import printer from "@/printer";

export default {
  components: {
    MainLayout,
    PackageInfo
  },
  data() {

    const defaultGrouping = {
      value: 'date', 
      label: this.$t('common.date'), 
      icon: 'bx-calendar-event', 
      get: () => this.packagesByDate,
      format: (date) => moment(date).locale(this.$i18n.locale).format('LL')
    }
    
    const defaultFilter = {value: null, label: this.$t('stores.filters.all'), icon: null, filter: () => this.packages}

    return {
      namespace: null,
      loading: false,
      barcode: null,
      location: null,
      packages: [],
      stats: null,
      package: null,
      locations: [],
      filterBy: defaultFilter,
      groupBy: defaultGrouping,
      reversed: false,
      defaultFilter,
      defaultGrouping,
    }
  },
  computed: {
    internalBarcode: {
      get() {
        return this.barcode
      },
      set(value) {
        this.barcode = value.replace(/\s/g, '').toUpperCase()
      }
    },

    groupingOptions() {
      const options = [this.defaultGrouping]

      if(this.isWarehouse) {
        options.push({
          value: 'location', 
          label: this.$t('common.destination'), 
          icon: 'bx-map', 
          get: () => this.packagesByLocation
        })
      }

      if(this.hasInventory) {
        options.push({
          value: 'inventory', 
          label: this.$t('stores.filters.inventory'), 
          icon: 'bx-grid-vertical', 
          get: () => this.packagesByInventory
        })
      }

      return options
    },
    inventoryFilterOptions() {
      const options = [this.defaultFilter]

      if(this.hasInventory) {
        options.push({
          value: 'unassigned', 
          label: this.$t('stores.filters.unassigned'), 
          icon: 'bx-error', 
          filter: () => this.packages.filter(p => !p.locker_number)
        })

        this.location.layout.map(z => options.push({
          value: z.code, 
          label: z.label, 
          icon: 'bx-area', 
          filter: () => this.packages.filter(p => p.locker_number?.startsWith(z.code))
        }))
      }

      return options
    },
    filteredPackages() {
      return this.filterBy.filter()
    },
    groupedPackages() {
      return this.groupBy.get()
    },
    orderedPackages() {
      return Object.fromEntries(
        Object.entries(this.groupedPackages)
          .sort(([a], [b]) => this.reversed ? b.localeCompare(a) : a.localeCompare(b))
      );
    },
    packagesByDate() {
      return this.filteredPackages.reduce((c,p) => {
        let date = moment(p.updated_at).locale(this.$i18n.locale).format('YYYY-MM-DD')
        if(!(date in c)) c[date] = []
        c[date].push(p)
        return c
      },{})
    },
    packagesByLocation() {
      return this.filteredPackages.reduce((c,p) => {
        let location = p.dropoff_location_name;
        if(!(location in c)) c[location] = []
        c[location].push(p)
        return c
      },{})
    },
    packagesByInventory() {
      return this.filteredPackages.reduce((c,p) => {
        let bin = p.locker_number?.substring(0,4) ?? this.$t('stores.filters.unassigned')
        if(!(bin in c)) c[bin] = []
        c[bin].push(p)
        return c
      },{})
    },
    infoDetail() {
      if(this.isPUDO) return false
      else return this.groupBy.value == 'date' ? 'location' : 'date'
    },
    hasInventory() {
      return !!(this.location?.features & FEATURE_INVENTORY)
    },
    isWarehouse() {
      return this.location?.class == CLASS_WAREHOUSE
    },
    isPUDO() {
      return this.location?.class == CLASS_PUDO
    },
    displayStats() {
      const stats = []

      if(this.stats?.in_store) {
        stats.push({value: this.stats.in_store, label: 'stores.stats.in-store'})
      }

      if(this.isPUDO && this.stats?.inbound) {
        stats.push({value: this.stats.inbound, label: 'stores.stats.inbound'})
      }

      if(this.isPUDO && this.stats?.pending) {
        stats.push({value: this.stats.pending, label: 'stores.stats.pending'})
      }

      if(this.hasInventory && this.stats?.in_store) {
        const percentage = Math.round((this.stats.located / this.stats.in_store) * 100)
        stats.push({value: `${percentage}%`, label: 'stores.stats.located'})
      }

      return stats
    },
  },
  mounted() {
    if(!this.locationId) {
      this.selectLocation()
    } else {
      this.getPackages()
    }
  },
  methods: {
    setGroupBy(option) {
      if(this.groupBy === option) {
        this.reversed = !this.reversed
      } else {
        this.groupBy = option
        this.reversed = false
      }
    },
    setInventoryFilter(option) {
      this.filterBy = option
    },
    async selectLocation() {
      if(!this.namespace) return
      const {code: id, label: name} = await this.$modal(SelectItem, {
        resourceName: 'locations',
        scope: ['dropoff',['class',this.namespace],'user']
      })

      if(id) {
        this.setLocation({id,name})
        this.focus()
        this.getPackages()
      }
    },
    async quickCheckin(item = null, chain = true) {
      await this.$modal(QuickCheckin, {
        namespace: this.namespace,
        locationId: this.locationId,
        hasInventory: this.hasInventory,
        item,
        chain,
        checkExisting: (barcode) => this.packages.find(p => p.barcode == barcode)
      })

      this.getPackages()
    },
    async viewLayout() {
      await this.$modal(InventoryLayout, {
        location: this.location,
        packages: this.packages,
      })
    },
    pasted() {
      setTimeout(() => {
        this.getPackage()
      },100)
    },
    focus() {
      this.$refs.barcode?.focus()
    },
    async getPackage() {
      // if(this.loading) return
      this.loading = true
      let reload = null

      try {
        const {data: {data,meta}} = await api.get(`${this.namespace}/${this.locationId}/get`,{
          params: {
            barcode: this.barcode
          }, 
          suppress: [404]
        })
  
        const item = new Package(data,meta)

        const result = await this.$modal(this.modal,{ item, location: this.location })

        if(this.hasInventory && (result === 'locate' || result === 'checkin')) {
          this.quickCheckin(item, false)
        }
        else if(result) {
          await this.$bvModal.msgBoxOk(this.$t(`${this.namespace}.success.${result}`),{
            autoFocusButton: 'ok',
            centered: true,
          })

          reload = true
        }
      } catch (error) {
        vibrate()
        await this.$bvModal.msgBoxOk(this.$t(`${this.namespace}.errors.not-found`),{
          autoFocusButton: 'ok',
          okVariant: 'danger'
        })
      } finally {
        this.loading = false
        this.barcode = null
        this.focus()
        if(reload) {
          await this.getPackages()
        }
      }
    },
    async getPackages() {
      this.loading = true
        this.packages = []
        this.stats = null

      try {
        let {data: {data, location, stats = null}} = await api.get(`${this.namespace}/${this.locationId}/packages`)

        this.backfillZoneLabels(location)

        this.packages = data
        this.stats = stats
        this.location = location
      } finally {
        this.loading = false
      }
    },
    openPackage(item) {
      this.barcode = item.barcode 
      this.pasted()
    },
    preventIllegal(e) {
      /[A-Z0-9]/.test(e.key.toUpperCase()) || e.preventDefault()
    },
    backfillZoneLabels(location) {
      location.layout = location.layout?.map(z => ({
        ...z,
        label: z.label 
          ? this.$t('stores.inventory.zone-with-label',{code: z.code, label: z.label}) 
          : this.$t('stores.inventory.zone',{code: z.code})
      }))
    },
    async downloadLabels() {
        let {data} = await api.get(`${this.namespace}/${this.locationId}/download-labels`,{
          responseType: 'blob',
        })

        printer.$emit('shipment:print',new Blob([data], {type: 'application/pdf'}))
    }
  }
}
</script>